import {
    Alert,
    Container,
    convertFromBoolean,
    convertToBoolean,
    Divider,
    Form,
    FormRadio,
    getPageValidationInfo,
    Header,
    SummaryGroup,
    useAlignments,
    useSizes,
} from 'metrix-common3';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ProfileSupernovaCommercialLossPayee from './LossPayee';

const ProfileSupernovaCommercialLossPayees = (props) => {
    const alignments = useAlignments();
    const sizes = useSizes();

    const [mode, setMode] = useState('lossPayeesView');
    const [currentLossPayee, setCurrentLossPayee] = useState(null);
    const [currentHasLossPayees, setCurrentHasLossPayees] = useState(null);

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        const pageHasTooltipInfo =
            props.tooltipInfo?.pages?.length && props.tooltipInfo.pages.some((p) => p.code === props.pageCode);
        if (!pageHasTooltipInfo) {
            props.doGetTooltipInfo(props.policy.product);
        }

        setCurrentHasLossPayees(props?.policy?.lossPayeesInfo?.hasLossPayees ?? false);
    }, []);

    const handleSave = () => {
        props.doReducePolicyElement({ code: 'lossPayeesInfo.hasLossPayees', value: currentHasLossPayees });

        if (currentHasLossPayees === true && props.policy?.lossPayees && props.policy.lossPayees.length === 0) {
            props.doExecutePolicyAction({ code: 'SN-COMMERCIAL.CREATELOSSPAYEE' });
        } else if (currentHasLossPayees === false && props.policy?.lossPayees && props.policy?.lossPayees.length > 0) {
            props.doExecutePolicyAction({ code: 'SN-COMMERCIAL.DELETELOSSPAYEES' });
        } else {
            props.doUpsertPolicy();
        }
    };

    const getLossPayees = () => {
        const lossPayees = [];

        if (props.policy?.lossPayees && props.policy.lossPayees.length > 0) {
            props.policy.lossPayees.forEach((lossPayee) => {
                const option = { code: 'lossPayeeOption', value: lossPayee.guid };

                option.elements = [];
                option.elements.push({ code: 'Label', value: lossPayee.label });

                lossPayees.push(option);
            });
        }

        return lossPayees;
    };

    const handleHasLossPayeesFormRadio = (value) => {
        const valueAsBollean = convertToBoolean(value.code);
        setCurrentHasLossPayees(valueAsBollean);

        props.doReducePolicyElement({
            code: 'lossPayeesInfo.hasLossPayees',
            value: valueAsBollean,
        });
    };

    const { error, warning } = getPageValidationInfo(props.pageCode, props.validationInfo);
    const lossPayees = getLossPayees();

    return (
        <React.Fragment>
            {(error.message.length !== 0 || warning.message.length !== 0) && (
                <Container top={sizes.md}>
                    {error.message.length !== 0 && <Alert mode='danger' header={error.title} items={error.message} />}
                    {warning.message.length !== 0 && (
                        <Alert mode='warning' header={warning.title} items={warning.message} />
                    )}
                </Container>
            )}
            {mode === 'lossPayeesView' && (
                <React.Fragment>
                    <Form
                        pageCode={props.pageCode}
                        sectionCode='lossPayeesInfo'
                        saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                        reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                        tooltipInfo={props.tooltipInfo}
                        validationInfo={props.validationInfo}
                        header={<FormattedMessage id='Profile_SupernovaCommercial_LossPayees.lossPayeesInfo' />}
                        label={<FormattedMessage id='Profile_SupernovaCommercial_LossPayees.details' />}
                        allowEdit={props.allowEdit}
                        saveEvent={() => handleSave()}
                        cancelEvent={() => props.doGetPolicy()}
                    >
                        <FormRadio
                            fieldCode='hasLossPayees'
                            label={<FormattedMessage id='Profile_SupernovaCommercial_LossPayees.haveAnyLossPayees' />}
                            value={
                                props.policy.lossPayeesInfo?.hasLossPayees !== undefined && {
                                    code: convertFromBoolean(props.policy.lossPayeesInfo.hasLossPayees),
                                }
                            }
                            references={props.reference?.references.YesNo}
                            referencesEvent={() => props.reference.doGetReferences('YesNo')}
                            changeEvent={(value) => handleHasLossPayeesFormRadio(value)}
                            allowEdit
                        />
                    </Form>
                    <Divider top={sizes.xl} />
                    <Header
                        size={sizes.lg}
                        textAlign={alignments.left}
                        text={<FormattedMessage id='Profile_SupernovaCommercial_LossPayees.lossPayees' />}
                    />
                    <Container top={sizes.xl} bottom={sizes.xl}>
                        <SummaryGroup
                            allowAdd={
                                props.allowEdit &&
                                props.policy.lossPayeesInfo?.hasLossPayees &&
                                (props.policy.lossPayees === undefined || lossPayees.length < 5)
                            }
                            addEvent={() => {
                                props.doExecutePolicyAction({ code: 'SN-COMMERCIAL.CREATELOSSPAYEE' });
                            }}
                            allowEdit={true}
                            editEvent={(lossPayee) => {
                                setCurrentLossPayee(lossPayee);
                                setMode('lossPayeeView');
                            }}
                            allowDelete={props.allowEdit && lossPayees.length > 0}
                            deleteEvent={(lossPayee) => {
                                props.doReduceParameter({ code: 'LossPayeeGuid', value: lossPayee });
                                props.doExecutePolicyAction({ code: 'SN-COMMERCIAL.DELETELOSSPAYEE' });
                            }}
                            entities={lossPayees}
                            icon='person'
                        />
                    </Container>
                </React.Fragment>
            )}
            {mode === 'lossPayeeView' && (
                <ProfileSupernovaCommercialLossPayee
                    rowId={currentLossPayee}
                    pageCode={props.pageCode}
                    saveTooltipInfo={() => props.doUpsertTooltipInfo(props.policy.product)}
                    reduceTooltipInfoElement={props.doReduceTooltipInfoElement}
                    tooltipInfo={props.tooltipInfo}
                    validationInfo={props.validationInfo}
                    lossPayee={props.policy.lossPayees.find((x) => x.guid === currentLossPayee)}
                    dotProp={`lossPayees.${props.policy.lossPayees.findIndex((x) => x.guid === currentLossPayee)}.`}
                    backEvent={() => {
                        setMode('lossPayeesView');
                        setCurrentHasLossPayees(null);
                    }}
                    doUpsertPolicy={props.doUpsertPolicy}
                    doGetPolicy={props.doGetPolicy}
                    doReduceParameter={props.doReduceParameter}
                    doReducePolicyElement={props.doReducePolicyElement}
                    doExecutePolicyAction={props.doExecutePolicyAction}
                    reference={props.reference}
                    context={props.context}
                    allowEdit={props.allowEdit}
                />
            )}
        </React.Fragment>
    );
};

export default ProfileSupernovaCommercialLossPayees;
